/**
 * Theme Mode Context
 *
 * Shares the current theme mode and toggle function across the component tree
 * so any component can read or change it without prop-drilling.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from '../theme';
import { useThemeMode, type ThemeMode } from '../hooks/useThemeMode';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

/**
 * Combines theme mode detection / persistence with MUI's ThemeProvider.
 * Wrap your app with this instead of the raw MUI ThemeProvider.
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const { mode, toggleMode } = useThemeMode();
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}

/**
 * Access the current theme mode and toggle function.
 */
export function useThemeModeContext(): ThemeModeContextType {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeModeContext must be used within a ThemeModeProvider');
  }
  return ctx;
}
